import { mapStandaloneItems, splitMenuList, sortByMenuOrder } from '../../../utils/helpers/splitMenuList';
import { SETTINGS_CATEGORY } from './menuTabConfig';

// Builds the tab/column structure NavMenu and MobileMenu both render, straight
// from the flat backend menu array (localStorage.leftNavMenu).
//
// menuOrder convention (4 digits, ABCD), once the backend adopts it:
//   A  (thousands) = tab position, left to right
//   B  (hundreds)  = column position within the tab, left to right — but the
//                    real grouping key is each item's own `parent`; B only
//                    decides which parent-group sits first (left) vs second
//   CD (tens+ones) = order of the item within its column (00-99)
//
// Items with menuOrder < 1000 (not migrated yet) fall back to one tab per
// `parent`, single column, ordered by their raw menuOrder — the simple
// behavior this replaces, so nothing breaks mid-migration.
const ABCD_THRESHOLD = 1000;

const decodeMenuOrder = (menuOrder) => {
  if (typeof menuOrder !== 'number' || menuOrder < ABCD_THRESHOLD) return null;
  return {
    tab: Math.floor(menuOrder / 1000) % 10,
    column: Math.floor(menuOrder / 100) % 10,
    order: menuOrder % 100
  };
};

const itemOrder = (item) => (item.decoded ? item.decoded.order : item.menuOrder);

const buildColumns = (items) => {
  const byParent = new Map();
  items.forEach((item) => {
    if (!byParent.has(item.parent)) byParent.set(item.parent, []);
    byParent.get(item.parent).push(item);
  });

  return [...byParent.entries()]
    .map(([parent, groupItems]) => ({
      key: parent,
      sortDigit: Math.min(...groupItems.map((item) => item.decoded?.column ?? 0)),
      items: groupItems
    }))
    .sort((a, b) => a.sortDigit - b.sortDigit)
    .map(({ key, items: groupItems }) => ({ key, items: groupItems }));
};

export const isTabActive = (tab, pathname, lang) =>
  tab.columns.some((column) => column.items.some((child) => pathname === `${child.optionPath}/${lang}`));

export const buildMenuTabs = (list) => {
  // Settings is never a NavMenu tab (it lives in the account gear icon) — drop it
  // by parent before bucketing so its legacy menuOrder can never collide with
  // another category's decoded ABCD tab digit and steal that tab's category.
  const menuList = Array.isArray(list) ? list.filter((item) => item.parent !== SETTINGS_CATEGORY) : list;
  const { standaloneItems, childItems } = splitMenuList(menuList);

  const standaloneTabs = mapStandaloneItems(standaloneItems, 'columns');

  const buckets = [];
  childItems.forEach((item) => {
    const decoded = decodeMenuOrder(item.menuOrder);
    const bucketKey = decoded ? `tab-${decoded.tab}` : `legacy-${item.parent}`;
    let bucket = buckets.find((entry) => entry.bucketKey === bucketKey);
    if (!bucket) {
      bucket = { bucketKey, items: [] };
      buckets.push(bucket);
    }
    bucket.items.push({ ...item, decoded });
  });

  const groupedTabs = buckets.map((bucket) => {
    const columns = buildColumns(bucket.items);
    columns.forEach((column) => column.items.sort((a, b) => itemOrder(a) - itemOrder(b)));

    const tabDigits = bucket.items.map((item) => item.decoded?.tab ?? 0);

    return {
      category: columns[0].key,
      optionPath: '',
      menuOrder: Math.min(...tabDigits),
      columns
    };
  });

  return [...standaloneTabs, ...groupedTabs].sort(sortByMenuOrder);
};

export default buildMenuTabs;
